'use client';

import * as React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from '@/context/auth-context';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Edit, Save, Camera, Shield } from 'lucide-react';
import { Separator } from './ui/separator';
import { Label } from './ui/label';
import { Icons } from './icons';
import CityStats from './city-stats';
import { Badge } from './ui/badge';
import AuthGate from './auth-gate';
import Link from 'next/link';


const ProfileEditor = () => {
    const { user, updateUserProfile, loading } = useAuth();
    const { toast } = useToast();
    const [newName, setNewName] = React.useState(user?.displayName || '');
    const [newPhoto, setNewPhoto] = React.useState<string | null>(user?.photoURL || null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setNewPhoto(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveChanges = async () => {
        if (!user) return;
        
        const nameChanged = newName !== user.displayName;
        const photoChanged = newPhoto !== user.photoURL;

        if (!nameChanged && !photoChanged) {
            toast({ title: "No changes to save.", variant: "default" });
            return;
        }
        if (newName.length < 2) {
             toast({ title: "Username must be at least 2 characters.", variant: "destructive" });
            return;
        }

        try {
            await updateUserProfile(newName, newPhoto);
            toast({ title: "Profile updated successfully!", variant: "default" });
        } catch (err: any) {
            toast({ title: "Failed to update profile.", description: err.message, variant: "destructive" });
        }
    };


    return (
        <div className="space-y-4 pt-4">
            <Separator />
            <h3 className="text-lg font-medium text-center">Edit Profile</h3>
            <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                    <Avatar className="h-24 w-24">
                        <AvatarImage src={newPhoto || undefined} />
                        <AvatarFallback>{newName?.[0] || user?.email?.[0]}</AvatarFallback>
                    </Avatar>
                     <Button
                        size="icon"
                        className="absolute bottom-0 right-0 rounded-full"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Camera className="h-4 w-4" />
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                </div>
                 <div className="w-full space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                        id="username"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Your Name"
                    />
                </div>
                <Button onClick={handleSaveChanges} disabled={loading} className="w-full">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default function ProfileView() {
  const { user, logout, isOwner, isEarlySupporter } = useAuth();
  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    if (!user) {
        setIsEditing(false);
    }
  }, [user]);
  
  if (!user) {
    return <AuthGate />;
  }

  return (
    <div className="p-4 md:p-8 flex flex-col items-center space-y-4">
      <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center">
          <Avatar className="h-24 w-24 mb-4">
              <AvatarImage src={user.photoURL || undefined} />
              <AvatarFallback>{user.displayName?.[0] || user.email?.[0]}</AvatarFallback>
          </Avatar>
          <CardTitle>{user.displayName || "User"}</CardTitle>
          <div className="flex items-center justify-center gap-2 mt-1">
              {isOwner && <Badge variant="secondary" className="border-primary/50 bg-primary/20 text-primary">Owner</Badge>}
              {isEarlySupporter && <Badge variant="secondary" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-600">Early Supporter</Badge>}
          </div>
          <CardDescription>{user.email}</CardDescription>
          </CardHeader>
          <CardContent>
          {isEditing ? (
              <ProfileEditor />
          ) : (
              <div className="flex flex-col gap-2">
                   {isOwner && (
                    <Button asChild variant="outline" className="w-full">
                        <Link href="/admin">
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                        </Link>
                    </Button>
                  )}
                  <Button onClick={() => setIsEditing(true)} className="w-full">
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Profile
                  </Button>
                  <Button onClick={logout} className="w-full" variant="destructive">Logout</Button>
              </div>
          )}
          </CardContent>
      </Card>
      <CityStats />
    </div>
  );
}
